import React, { useState, useMemo } from 'react';
import { MapPin, Truck as TruckIcon, ShieldCheck, Star, Clock, Plus, Search, Phone, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { generateTrucks, indianCities, truckTypes } from '../mock/mockData';
import { toast } from '../hooks/use-toast';
import { trucksApi } from '../lib/api';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
const timeAgo = (m) => m < 60 ? `${m}m ago` : m < 1440 ? `${Math.floor(m/60)}h ago` : `${Math.floor(m/1440)}d ago`;

const adaptTruck = (t) => ({
  id: t.id,
  currentLocation: t.current_location,
  preferredDestination: t.preferred_destination,
  truckType: t.truck_type,
  truckCode: t.truck_code,
  capacity: t.capacity,
  vehicleNumber: t.vehicle_number,
  owner: t.owner_company,
  ownerContact: t.owner_name,
  ownerPhone: t.owner_phone,
  rating: t.rating,
  verified: t.verified,
  availability: t.availability,
  expectedRate: t.expected_rate,
  postedMinutesAgo: Math.max(1, Math.floor((Date.now() - new Date(t.created_at).getTime()) / 60000)),
});

export default function TruckBoard() {
  const [trucks, setTrucks] = useState(() => generateTrucks(24));
  const [filters, setFilters] = useState({ loc: '', dest: '', type: '', search: '' });
  const [postOpen, setPostOpen] = useState(false);
  const [newTruck, setNewTruck] = useState({ currentLocation: '', preferredDestination: '', truckType: '', vehicleNumber: '', expectedRate: '' });

  React.useEffect(() => {
    trucksApi.list().then(data => {
      if (data && data.length > 0) {
        setTrucks(prev => [...data.map(adaptTruck), ...prev]);
      }
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => trucks.filter(t => {
    const s = filters.search.toLowerCase();
    return (!filters.loc || t.currentLocation === filters.loc) &&
      (!filters.dest || t.preferredDestination === filters.dest) &&
      (!filters.type || t.truckCode === filters.type) &&
      (!s || t.currentLocation.toLowerCase().includes(s) || t.preferredDestination.toLowerCase().includes(s) || t.vehicleNumber.toLowerCase().includes(s));
  }), [trucks, filters]);

  const post = async () => {
    if (!newTruck.currentLocation || !newTruck.preferredDestination) {
      toast({ title: 'Missing fields', description: 'Please fill current location and destination.' });
      return;
    }
    const tt = truckTypes.find(t => t.code === newTruck.truckType) || truckTypes[0];
    const payload = {
      current_location: newTruck.currentLocation,
      preferred_destination: newTruck.preferredDestination,
      truck_type: tt.label,
      truck_code: tt.code,
      capacity: tt.capacity,
      vehicle_number: newTruck.vehicleNumber || 'MH 12 AB 3456',
      expected_rate: parseInt(newTruck.expectedRate) || 30000,
    };
    try {
      const created = await trucksApi.create(payload);
      setTrucks([adaptTruck(created), ...trucks]);
      setPostOpen(false);
      setNewTruck({ currentLocation: '', preferredDestination: '', truckType: '', vehicleNumber: '', expectedRate: '' });
      toast({ title: 'Truck posted!', description: `Your truck is now visible to shippers.` });
    } catch (err) {
      toast({ title: 'Failed to post', description: err?.response?.data?.detail || 'Please sign in first.' });
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0B2545] font-[Poppins]">Truck Board</h1>
          <p className="text-sm text-gray-500 mt-1">{filtered.length.toLocaleString('en-IN')} trucks available across India</p>
        </div>
        <Dialog open={postOpen} onOpenChange={setPostOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#00A651] hover:bg-[#008a43] text-white"><Plus className="w-4 h-4 mr-2" />Post Your Truck</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Post Available Truck</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label className="text-sm">Current Location *</Label>
                <Select value={newTruck.currentLocation} onValueChange={v => setNewTruck({ ...newTruck, currentLocation: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="max-h-64">{indianCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Preferred Destination *</Label>
                <Select value={newTruck.preferredDestination} onValueChange={v => setNewTruck({ ...newTruck, preferredDestination: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="max-h-64">{indianCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Truck Type</Label>
                <Select value={newTruck.truckType} onValueChange={v => setNewTruck({ ...newTruck, truckType: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{truckTypes.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Vehicle Number</Label>
                <Input placeholder="MH 12 AB 3456" className="mt-1.5" value={newTruck.vehicleNumber} onChange={e => setNewTruck({ ...newTruck, vehicleNumber: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-sm">Expected Rate (₹)</Label>
                <Input type="number" placeholder="30000" className="mt-1.5" value={newTruck.expectedRate} onChange={e => setNewTruck({ ...newTruck, expectedRate: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPostOpen(false)}>Cancel</Button>
              <Button onClick={post} className="bg-[#00A651] hover:bg-[#008a43] text-white">Post Truck</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="grid md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search trucks..." className="pl-9" value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} />
          </div>
          <Select value={filters.loc} onValueChange={v => setFilters({ ...filters, loc: v === 'all' ? '' : v })}>
            <SelectTrigger><SelectValue placeholder="Current location" /></SelectTrigger>
            <SelectContent className="max-h-64"><SelectItem value="all">All locations</SelectItem>{indianCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.dest} onValueChange={v => setFilters({ ...filters, dest: v === 'all' ? '' : v })}>
            <SelectTrigger><SelectValue placeholder="Destination" /></SelectTrigger>
            <SelectContent className="max-h-64"><SelectItem value="all">All destinations</SelectItem>{indianCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.type} onValueChange={v => setFilters({ ...filters, type: v === 'all' ? '' : v })}>
            <SelectTrigger><SelectValue placeholder="Truck type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All trucks</SelectItem>{truckTypes.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {filtered.map(t => (
          <div key={t.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover-lift">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#0B2545]/5 flex items-center justify-center">
                  <TruckIcon className="w-6 h-6 text-[#0B2545]" />
                </div>
                <div>
                  <p className="font-bold text-[#0B2545]">{t.truckType}</p>
                  <p className="text-xs text-gray-500 font-mono">{t.vehicleNumber}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                {t.verified && <Badge className="bg-[#00A651]/10 text-[#00A651] border-0 text-[10px]"><ShieldCheck className="w-3 h-3 mr-1" />Verified</Badge>}
                <span className="text-[10px] text-gray-400">{timeAgo(t.postedMinutesAgo)}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-3 text-sm">
              <MapPin className="w-4 h-4 text-[#00A651]" />
              <span className="font-semibold text-[#0B2545]">{t.currentLocation}</span>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <span className="font-semibold text-[#0B2545]">{t.preferredDestination}</span>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
              <div className="bg-gray-50 rounded p-2">
                <p className="text-gray-500">Capacity</p>
                <p className="font-semibold text-[#0B2545]">{t.capacity}</p>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <p className="text-gray-500">Available</p>
                <p className="font-semibold text-[#00A651]">{t.availability}</p>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <p className="text-gray-500">Rate</p>
                <p className="font-semibold text-[#0B2545]">{formatINR(t.expectedRate)}</p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <div>
                <p className="text-xs text-gray-500">{t.owner}</p>
                <div className="flex items-center gap-1 text-xs"><Star className="w-3 h-3 text-[#FFB800] fill-[#FFB800]" />{t.rating}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => toast({ title: 'Calling driver...', description: t.ownerPhone })}><Phone className="w-4 h-4" /></Button>
                <Button size="sm" className="bg-[#00A651] hover:bg-[#008a43] text-white" onClick={() => toast({ title: 'Enquiry sent!', description: `To ${t.owner}` })}>Hire Truck</Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
